import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
  Stack,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  KeyboardArrowRight,
  KeyboardArrowDown,
  AccessTime
} from '@mui/icons-material';
import {
  ExternalLink,
  AlertCircle,
  Lightbulb,
  Wrench,
  Info
} from 'lucide-react';
import { Sidebar } from 'primereact/sidebar';
import MonacoEditorWithFullscreen from '../MonacoEditorWithFullscreen';

const BuildLogs = ({
  initialFetch,
  fetchBuildLogs,
  goToAllBuilds,
  isLoading,
  hideAllBuilds,
  getStatus,
  root = false,
  isExpanded
}) => {
  const [buildLogs, setBuildLogs] = useState({});
  const [buildLogIds, setBuildLogIds] = useState([]);

  const [errorSidebarVisible, setErrorSidebarVisible] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const isInitial = useRef(true);

  const getStatusColor = (statusObj, theme) => {
    let statusColor = statusObj?.status?.props?.style?.color;
    return statusColor || theme.palette.info.main;
  };

  const LogContent = ({ content, logMediaUrl }) => {
    if (!content) return null;
    if (MonacoEditorWithFullscreen) {
      return (
        <Box sx={{
          minHeight: 200, 
          width: '100%'
        }}>
          <MonacoEditorWithFullscreen
            value={content}
            language="json"
            theme="vs-dark"
            logMediaUrl={logMediaUrl}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              lineNumbers: 'on',
              renderLineHighlight: 'none',
              scrollBeyondLastLine: false,
              fontSize: 13,
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              wordWrap: 'on',
              automaticLayout: true,
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible',
                verticalScrollbarSize: 14,
                horizontalScrollbarSize: 14,
                verticalSliderSize: 14,
                horizontalSliderSize: 14,
              },
              overviewRulerBorder: false,
              hideCursorInOverviewRuler: true,
            }}
            height="200px"
          />
        </Box>
      );
    }

    const lines = content.split('\n');
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
        fontSize: '14px',
        lineHeight: '1.5',
        bgcolor: '#1e1e1e',
        color: '#d4d4d4',
        position: 'relative'
      }}>
        {logMediaUrl && (
          <Tooltip title="Open" placement='top'>
            <div
              className="absolute right-2 top-2 bg-gray-50 text-black px-2 py-1 border rounded-full flex items-center justify-center cursor-pointer 
               hover:bg-gray-200 transition-all duration-200"
              onClick={() => logMediaUrl && window.open(logMediaUrl, "_blank")}
            >
              <ExternalLink color="#000" size={14} /> <span className='ml-1'>Show Full</span>
            </div>
          </Tooltip>
        )}
        {lines.map((line, idx) => (
          <Box key={idx} sx={{ display: 'flex', py: 0.5 }}>
            <Box sx={{
              color: '#858585',
              minWidth: '3em',
              textAlign: 'right',
              userSelect: 'none'
            }}>
              {idx + 1}
            </Box>
            <Box sx={{
              flex: 1, whiteSpace: 'pre-wrap',
              wordBreak: "break-all",
              px: 2,
            }}>
              {line}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const BuildLogStep = ({ logId, depth = 0, isLastLevel = false }) => {
    const log = buildLogs[logId];
    if (!log) return null;

    const statusObj = getStatus(log?.status);
    const currentStatus = statusObj?.status || '';
    const statusIconComponent = statusObj?.statusIcon;
    const hasChildren = log.has_child_logs;
    const isExpanded = log.isExpanded;

    const showDropdown = hasChildren;
    const nextIsLast = hasChildren && !buildLogs[log.childLogIds[0]]?.has_child_logs;
    const showEditorStyle = isLastLevel;

    const getAllChildContent = () => {
      if (!hasChildren || !nextIsLast) return '';
      return log.childLogIds
        .map(childId => buildLogs[childId]?.log_message)
        .filter(Boolean)
        .join('\n');
    };

    const getLogFileUrl = () => {
      if (!hasChildren || !nextIsLast) return null;
      const urls = log.childLogIds
        ?.map(childId => buildLogs[childId]?.log_file_url)
        .filter(Boolean);
      return urls.length > 0 ? urls[0] : null;
    };

    const handleExpand = (e) => {
      e.stopPropagation();
      onExpandLog(e, logId);
    };

    return (
      <Paper
        elevation={0}
        sx={{
          mb: showEditorStyle ? 0 : 2,
          border: '1px solid',
          borderColor: theme => depth > 0 ? ' var(--borderColor)' : getStatusColor(statusObj, theme),
          borderRadius: '4px',
          overflow: 'hidden',
          ml: depth > 0 ? 4 : 0,
          mr: depth > 0 ? 4 : 0,
          bgcolor: showEditorStyle ? '#1e1e1e' : 'background.paper',
        }}
      >
        <Box
          sx={{
            p: 2,
            cursor: showDropdown ? 'pointer' : 'default'
          }}
          onClick={showDropdown ? handleExpand : undefined}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {showDropdown && (
              <IconButton
                size="small"
                sx={{ color: showEditorStyle ? '#d4d4d4' : 'inherit' }}
              >
                {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
              </IconButton>
            )}

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: depth === 0 ? 500 : 400,
                  color: showEditorStyle ? '#d4d4d4' : 'text.primary'
                }}
              >
                {log.log_message}
              </Typography>
            </Box>

            {!showEditorStyle && (
              <Stack direction="row" spacing={2} alignItems="center">
                {log.execution_time > 0 && (
                  <>
                    {/* {log?.status === "fail" && (
                      <Button
                        startIcon={<Info size={15} />}
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setSelectedError(log);
                          console.log(log);
                          setErrorSidebarVisible(true);
                        }}
                      >
                        Fix Me
                      </Button>
                    )} */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTime fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {log.execution_time.toFixed(2)}s
                      </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                  </>
                )}
                <Stack direction="row" spacing={1} alignItems="center">
                  {statusIconComponent}
                  <Typography variant="body2" color="text.secondary">
                    {currentStatus}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Box>

        <Collapse in={isExpanded && hasChildren}>
          {hasChildren && (
            <Box
              sx={{
                ...(nextIsLast && {
                  bgcolor: '#1e1e1e',
                  color: "var(--text-secondary)",
                  p: 0,
                  mx: 0,
                })
              }}
            >
              {nextIsLast ? (
                <LogContent content={getAllChildContent()} logMediaUrl={getLogFileUrl()} />
              ) : (
                log.childLogIds?.map((childId) => (
                  <BuildLogStep
                    key={childId}
                    logId={childId}
                    depth={depth + 1}
                    isLastLevel={nextIsLast}
                  />
                ))
              )}
            </Box>
          )}
        </Collapse>
      </Paper>
    );
  };

  useEffect(() => {
    if (initialFetch && isInitial.current) {
      fetchLogs();
    }
  }, [initialFetch]);

  const fetchLogs = async (parentLogId) => {
    const response = await fetchBuildLogs(parentLogId);
    if (response) {
      setParsedBuildLogs(response, parentLogId);
    }
  };

  const updateBuildLogs = (logs) => {
    setBuildLogs(logs);
  };

  const setParsedBuildLogs = async (logs, parentLogId) => {
    const logHash = { ...buildLogs };
    const logIds = logs.map((log) => {
      const logObj = { ...log, isExpanded: false };
      if (log.has_child_logs) {
        logObj.childLogIds = [];
      }
      logHash[log.id] = { ...logObj };
      return log.id;
    });

    if (parentLogId) {
      logHash[parentLogId] = {
        ...logHash[parentLogId],
        childLogIds: [...logIds],
        isExpanded: true
      };
    }

    updateBuildLogs(logHash);
    if (isInitial.current) {
      setBuildLogIds(logIds);
      isInitial.current = false;
    }
  };

  const onExpandLog = (event, parentLogId) => {
    event.stopPropagation();
    if (buildLogs?.[parentLogId]?.isExpanded) {
      updateBuildLogs({
        ...buildLogs,
        [parentLogId]: { ...buildLogs[parentLogId], isExpanded: false }
      });
    } else if (buildLogs?.[parentLogId]?.has_child_logs) {
      const childLogIds = buildLogs?.[parentLogId]?.childLogIds || [];
      const lastChildId = childLogIds[childLogIds.length - 1];
      if (lastChildId && buildLogs[lastChildId]) {
        updateBuildLogs({
          ...buildLogs,
          [parentLogId]: { ...buildLogs[parentLogId], isExpanded: true }
        });
      } else {
        fetchLogs(parentLogId);
      }
    }
  };

  if (isInitial.current || !buildLogIds) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (buildLogIds && buildLogIds.length === 0) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography sx={{ mb: 2 }}>
          Logs are getting ready, try refreshing!
        </Typography>
        {!hideAllBuilds && (
          <Button
            variant="outlined"
            onClick={goToAllBuilds}
          >
            All Builds
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {!hideAllBuilds && (
        <Button
          variant="outlined"
          onClick={goToAllBuilds}
          sx={{ mb: 2 }}
        >
          All Builds
        </Button>
      )}

      <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
        {buildLogIds.map((id) => (
          <BuildLogStep key={id} logId={id} depth={0} />
        ))}
      </Box>
      <Sidebar
        visible={errorSidebarVisible}
        position="right"
        onHide={() => setErrorSidebarVisible(false)}
        style={{ width: '500px' }}
        title='Error Analysis'
        className="p-sidebar-lg bgPrimary border !border-[var(--borderColor)] text-[var(--text-primary)]"
      >
        <div className="p-6">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              Error Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detailed explanation and suggested solutions
            </Typography>
          </Box>

          {selectedError && (
            <Box sx={{ width: '100%' }}>
              <Tabs
                value={selectedTab}
                onChange={(e, newValue) => setSelectedTab(newValue)}
                variant="fullWidth"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 3
                }}
              >
                <Tab
                  icon={<AlertCircle size={18} />}
                  label="Error Analysis"
                  iconPosition="start"
                  sx={{ textTransform: 'none' }}
                />
                <Tab
                  icon={<Wrench size={18} />}
                  label="Solutions"
                  iconPosition="start"
                  sx={{ textTransform: 'none' }}
                />
              </Tabs>

              <Box sx={{ mt: 2 }}>
                {selectedTab === 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Error Details
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 1
                      }}
                    >
                      <Typography variant="body2" color="var(--text-primary)" sx={{ mb: 2 }}>
                        {selectedError.log_message}
                      </Typography>
                    </Paper>

                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Causes
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 1
                      }}
                    >
                      <Stack spacing={1.5}>
                        {[
                          'Changes in Response Structure',
                          'Configuration mismatch in the test environment',
                          'Changes in API Endpoints Data',
                          'API endpoint availability issues',
                          'Authentication or authorization problems',
                          'You have not set authorization headers in the test plan',
                        ].map((cause, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              • {cause}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  </>
                )}

                {selectedTab === 1 && (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Suggested Actions
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 1
                      }}
                    >
                      <Stack spacing={1.5}>
                        {[
                          'Verify test environment configuration',
                          'Check test data validity',
                          'Ensure API endpoints are accessible',
                          'Validate authentication credentials'
                        ].map((action, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              • {action}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  </>
                )}

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setErrorSidebarVisible(false)}
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          )}
        </div>
      </Sidebar>
    </Box>
  );
};

export default BuildLogs;